import { useMemo, useEffect } from "react";
import {
    Box,
    Dialog,
    DialogContent,
    FormControl,
    FormHelperText,
    IconButton,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Button as FoodsButton, FoodsIcon, RichTextEditor } from "@foodpilot/foods";
import { useSnackbar } from "src/utils/useSnackbar";
import {
    EntityTargetScoreAssignment,
    EntityTargetScoreAssignmentWrite,
    useCreateOrUpdateEntityTargetScoreAssignment,
} from "src/api/client-api/entityTargetScoreAssignment/useEntityTargetScoreAssignment";
import { useGetAllScoreType } from "src/api/client-api/scoreType/useGetAllScoreType";

// Zod schema for EntityTargetScoreAssignment form validation
const EntityTargetScoreAssignmentFormSchema = z.object({
    id: z.number().optional(),
    label: z.string().min(1, "Label is required"),
    identifier: z
        .string()
        .min(1, "Identifier is required")
        .regex(/^[a-z0-9_]+$/, "Identifier can only contain lowercase letters, numbers, and underscores."),
    entityClass: z.string().min(1, "'Applies To' field is required"),
    value: z.number().nullable().optional(),
    description: z.string().optional(),
    scoreId: z.number(),
});

type EntityTargetScoreAssignmentFormData = z.infer<typeof EntityTargetScoreAssignmentFormSchema>;

type EntityTargetScoreAssignmentModalProps = {
    open: boolean;
    onClose: () => void;
    assignment?: EntityTargetScoreAssignment;
    onSuccess?: (assignment: EntityTargetScoreAssignment) => void;
};

export const EntityTargetScoreAssignmentModal = (props: EntityTargetScoreAssignmentModalProps) => {
    const { open, onClose, assignment, onSuccess } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const mutation = useCreateOrUpdateEntityTargetScoreAssignment();

    const APPLICABLE_ENTITIES = [
        { label: t("entity.company", "Company"), value: "App\\Core\\Tenancy\\Entity\\Organization" },
        {
            label: t("entity.company_sector", "Company sector"),
            value: "App\\Core\\Tenancy\\Entity\\OrganizationSector",
        },
        { label: t("entity.product", "Product"), value: "App\\Entity\\Product" },
        { label: t("entity.product_line", "Product line"), value: "App\\Entity\\ProductLine" },
        { label: t("entity.ingredient", "Ingredient"), value: "App\\Entity\\Ingredient" },
    ];

    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset,
        watch,
        setValue,
        resetField,
    } = useForm<EntityTargetScoreAssignmentFormData>({
        mode: "onChange",
        resolver: zodResolver(EntityTargetScoreAssignmentFormSchema),
        defaultValues: {
            id: undefined,
            label: "",
            identifier: "",
            entityClass: "",
            value: undefined,
            description: "",
            scoreId: undefined,
        },
    });

    const { scoreTypes, isLoading: isLoadingScoreTypes } = useGetAllScoreType();

    const selectedEntityClass = watch("entityClass");

    const mapEntityToScoreType = (
        entityClass?: string,
    ): "product" | "ingredient" | "packaging" | "company" | undefined => {
        if (!entityClass) return undefined;
        if (entityClass.includes("ProductLine") || entityClass.includes("Product")) return "product";
        if (entityClass.includes("Ingredient")) return "ingredient";
        if (entityClass.includes("Organization") || entityClass.includes("Company")) return "company";
        return undefined;
    };

    const availableScoreIds: number[] = useMemo(() => {
        if (!scoreTypes) return [];
        const mapped = mapEntityToScoreType(selectedEntityClass);
        if (mapped) {
            const st = scoreTypes.find((s) => s.type === mapped);
            return st?.scoreIds ?? [];
        }
        const all = scoreTypes.flatMap((s) => s.scoreIds ?? []);
        return Array.from(new Set(all));
    }, [scoreTypes, selectedEntityClass]);

    useEffect(() => {
        // Reset scoreId if it no longer matches available options after entity change
        const current = watch("scoreId");
        if (current !== undefined && !availableScoreIds.includes(Number(current))) {
            resetField("scoreId");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEntityClass, availableScoreIds]);

    useEffect(() => {
        if (!open) return;
        if (assignment) {
            reset({
                id: assignment.id,
                label: assignment.label,
                identifier: assignment.identifier,
                entityClass: assignment.entityClass,
                value: assignment.value,
                description: assignment.description ?? "",
                scoreId: assignment.scoreId,
            });
        } else {
            reset({
                id: undefined,
                label: "",
                identifier: "",
                entityClass: "",
                value: undefined,
                description: "",
                scoreId: undefined,
            });
        }
    }, [open, assignment, reset]);

    const onSubmitHandler: SubmitHandler<EntityTargetScoreAssignmentFormData> = (values) => {
        const payload: EntityTargetScoreAssignmentWrite = {
            id: assignment?.id,
            label: values.label,
            identifier: values.identifier,
            entityClass: values.entityClass,
            value: values.value,
            description: values.description,
            scoreId: Number(values.scoreId),
        };

        mutation.mutate(payload, {
            onSuccess: (response) => {
                const saved = response.data as EntityTargetScoreAssignment;
                if (onSuccess) onSuccess(saved);
                handleClose();
            },
            onError: () => {
                snackbar.setSnackbarProps({
                    message: t(
                        "settings.score.entity_target_score_assignments.error.saveFailed",
                        "Failed to save. Please try again.",
                    ),
                    type: "error",
                });
            },
        });
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: "16px", maxHeight: "90vh", width: "638px" } }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px 32px 0px 32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E" }}>
                        {assignment ?
                            t("settings.score.entity_target_score_assignments.edit", "Edit target score assignment")
                        :   t("settings.score.entity_target_score_assignments.add", "Add a target score assignment")}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    sx={{ padding: "8px", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
                >
                    <FoodsIcon icon="close" size={1.5} />
                </IconButton>
            </Box>

            <DialogContent sx={{ padding: "32px", backgroundColor: "#FEFCFB", overflowY: "auto" }}>
                <form onSubmit={handleSubmit(onSubmitHandler)}>
                    <Stack spacing="24px">
                        <Box>
                            <Stack spacing="24px">
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("settings.score.entity_target_score_assignments.label", "Label")}* :
                                    </Typography>
                                    <Controller
                                        name="label"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                placeholder={t(
                                                    "settings.score.entity_target_score_assignments.label.placeholder",
                                                    "Enter label",
                                                )}
                                                error={Boolean(errors.label)}
                                                helperText={errors.label?.message}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "8px",
                                                        backgroundColor: "white",
                                                        border: "1px solid #E7E6E4",
                                                        "& fieldset": { border: "none" },
                                                        "&:hover fieldset": { border: "none" },
                                                        "&.Mui-focused": {
                                                            border: (theme) =>
                                                                `1px solid ${theme.palette.primary[600]}`,
                                                        },
                                                        "&.Mui-focused fieldset": { border: "none" },
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        fontSize: "0.875rem",
                                                        fontWeight: 400,
                                                        lineHeight: "1.6em",
                                                        color: "#393A36",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("settings.score.entity_target_score_assignments.description", "Description")}{" "}
                                        :
                                    </Typography>
                                    <Controller
                                        name="description"
                                        control={control}
                                        render={({ field }) => (
                                            <RichTextEditor {...field} allowBase64Images initialContent={field.value} />
                                        )}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t(
                                            "settings.score.entity_target_score_assignments.entity_identifier",
                                            "Entity identifier",
                                        )}
                                        * :
                                    </Typography>
                                    <Controller
                                        name="identifier"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                placeholder={t(
                                                    "settings.score.entity_target_score_assignments.identifier.placeholder",
                                                    "Entity ID",
                                                )}
                                                error={Boolean(errors.identifier)}
                                                helperText={errors.identifier?.message}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "8px",
                                                        backgroundColor: "white",
                                                        border: "1px solid #E7E6E4",
                                                        "& fieldset": { border: "none" },
                                                        "&:hover fieldset": { border: "none" },
                                                        "&.Mui-focused": {
                                                            border: (theme) =>
                                                                `1px solid ${theme.palette.primary[600]}`,
                                                        },
                                                        "&.Mui-focused fieldset": { border: "none" },
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        fontSize: "0.875rem",
                                                        fontWeight: 400,
                                                        lineHeight: "1.6em",
                                                        color: "#393A36",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("settings.score.entity_target_score_assignments.appliesTo", "Applies to")}* :
                                    </Typography>
                                    <Controller
                                        name="entityClass"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl
                                                fullWidth
                                                error={Boolean(errors.entityClass)}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "8px",
                                                        backgroundColor: "white",
                                                        border: "1px solid #E7E6E4",
                                                        "& fieldset": { border: "none" },
                                                        "&:hover fieldset": { border: "none" },
                                                        "&.Mui-focused fieldset": { border: "none" },
                                                    },
                                                }}
                                            >
                                                <Select
                                                    {...field}
                                                    displayEmpty
                                                    sx={{
                                                        "& .MuiSelect-select": {
                                                            fontSize: "0.875rem",
                                                            fontWeight: 400,
                                                            lineHeight: "1.6em",
                                                            color: "#393A36",
                                                        },
                                                    }}
                                                >
                                                    <MenuItem value="" disabled>
                                                        {t(
                                                            "settings.score.entity_target_score_assignments.appliesTo.placeholder",
                                                            "Select an entity",
                                                        )}
                                                    </MenuItem>
                                                    {APPLICABLE_ENTITIES.map((e) => (
                                                        <MenuItem key={e.value} value={e.value}>
                                                            {e.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                <FormHelperText>{errors.entityClass?.message}</FormHelperText>
                                            </FormControl>
                                        )}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("settings.score.entity_target_score_assignments.value", "Value")} :
                                    </Typography>
                                    <Controller
                                        name="value"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                type="number"
                                                fullWidth
                                                value={field.value != null ? field.value.toString() : ""}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    if (v === "") {
                                                        field.onChange(null);
                                                    } else {
                                                        const numValue = Number(v);
                                                        field.onChange(isNaN(numValue) ? null : numValue);
                                                    }
                                                }}
                                                placeholder={t(
                                                    "settings.score.entity_target_score_assignments.value.placeholder",
                                                    "Enter numeric value (leave empty for 'Coming Soon')",
                                                )}
                                                error={Boolean(errors.value)}
                                                helperText={errors.value?.message}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "8px",
                                                        backgroundColor: "white",
                                                        border: "1px solid #E7E6E4",
                                                        "& fieldset": { border: "none" },
                                                        "&:hover fieldset": { border: "none" },
                                                        "&.Mui-focused": {
                                                            border: (theme) =>
                                                                `1px solid ${theme.palette.primary[600]}`,
                                                        },
                                                        "&.Mui-focused fieldset": { border: "none" },
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        fontSize: "0.875rem",
                                                        fontWeight: 400,
                                                        lineHeight: "1.6em",
                                                        color: "#393A36",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("settings.score.entity_target_score_assignments.score_id", "Score")}* :
                                    </Typography>
                                    <Controller
                                        name="scoreId"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl fullWidth error={Boolean(errors.scoreId)}>
                                                <Select
                                                    {...field}
                                                    displayEmpty
                                                    disabled={isLoadingScoreTypes}
                                                    value={field.value ?? ""}
                                                    onChange={(e) => {
                                                        const v = e.target.value as number | string;
                                                        field.onChange(v === "" ? undefined : Number(v));
                                                    }}
                                                    sx={{
                                                        "& .MuiSelect-select": {
                                                            fontSize: "0.875rem",
                                                            fontWeight: 400,
                                                            lineHeight: "1.6em",
                                                            color: "#393A36",
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "8px",
                                                            backgroundColor: "white",
                                                            border: "1px solid #E7E6E4",
                                                        },
                                                    }}
                                                >
                                                    <MenuItem value="" disabled>
                                                        {isLoadingScoreTypes ?
                                                            t("loading", "Loading...")
                                                        : availableScoreIds.length === 0 ?
                                                            t("no.options", "No score IDs available")
                                                        :   t(
                                                                "settings.score.entity_target_score_assignments.score_id.placeholder_select",
                                                                "Select a score ID",
                                                            )
                                                        }
                                                    </MenuItem>
                                                    {availableScoreIds.map((id) => (
                                                        <MenuItem key={id} value={id}>
                                                            {id}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                <FormHelperText>{errors.scoreId?.message}</FormHelperText>
                                            </FormControl>
                                        )}
                                    />
                                </Box>
                            </Stack>
                        </Box>
                    </Stack>
                </form>
            </DialogContent>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "16px",
                    padding: "32px",
                    borderTop: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <FoodsButton
                    variant="outlined"
                    onClick={handleClose}
                    sx={(theme) => ({
                        borderRadius: "44px",
                        padding: "0px 20px",
                        height: "40px",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        borderColor: theme.palette.primary[600],
                        color: theme.palette.primary[600],
                        backgroundColor: "white",
                        "&:hover": {
                            borderColor: theme.palette.primary[600],
                            backgroundColor: "rgba(47, 203, 212, 0.04)",
                        },
                    })}
                >
                    {t("cancel", "Cancel")}
                </FoodsButton>
                <FoodsButton
                    variant="contained"
                    type="submit"
                    onClick={handleSubmit(onSubmitHandler)}
                    disabled={!isValid || mutation.isLoading}
                    sx={(theme) => ({
                        borderRadius: "44px",
                        padding: "0px 20px",
                        height: "40px",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                        color: "white !important",
                        boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                        "&:hover": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                            boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                            color: "white !important",
                        },
                        "&:disabled": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            opacity: 0.36,
                            color: "white !important",
                        },
                    })}
                >
                    {t("save", "Save")}
                </FoodsButton>
            </Box>
        </Dialog>
    );
};

export default EntityTargetScoreAssignmentModal;
